/**
 * Error Boundary Components
 *
 * Provides graceful error handling for React components.
 * Prevents the entire app from crashing when a component throws an error.
 */

import React, { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { logger } from '../../utils/logger';

// ============================================================================
// BASE ERROR BOUNDARY
// ============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * Base Error Boundary
 * Generic error boundary with customizable fallback UI
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Component error caught by boundary', { error, errorInfo });
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    this.props.onReset?.();
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center p-8 bg-red-50 rounded-lg border border-red-200">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Something went wrong
          </h3>
          <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={this.handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// CHART ERROR BOUNDARY
// ============================================================================

interface ChartErrorBoundaryProps {
  children: ReactNode;
  chartName?: string;
  onRetry?: () => void;
}

/**
 * Chart Error Boundary
 * Specialized error boundary for chart/visualization components
 */
export class ChartErrorBoundary extends Component<ChartErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Chart rendering error', {
      chartName: this.props.chartName,
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onRetry?.();
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-amber-50 rounded-lg border border-amber-200">
          <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
          <h4 className="text-base font-semibold text-gray-900 mb-2">
            Chart failed to load
          </h4>
          <p className="text-sm text-gray-600 mb-4 text-center">
            {this.props.chartName || 'This chart'} encountered an error while rendering.
          </p>
          {this.props.onRetry && (
            <button
              onClick={this.handleRetry}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// PAGE ERROR BOUNDARY
// ============================================================================

interface PageErrorBoundaryProps {
  children: ReactNode;
  pageName?: string;
}

/**
 * Page Error Boundary
 * Full-page error boundary with navigation options
 */
export class PageErrorBoundary extends Component<PageErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Page error caught by boundary', {
      pageName: this.props.pageName,
      error,
      errorInfo,
    });
  }

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Page Error
              </h2>

              <p className="text-gray-600 mb-1">
                {this.props.pageName || 'This page'} encountered an unexpected error.
              </p>

              {this.state.error?.message && (
                <p className="text-sm text-gray-500 mb-6 font-mono bg-gray-50 p-3 rounded border border-gray-200 max-w-full overflow-x-auto">
                  {this.state.error.message}
                </p>
              )}

              <div className="flex gap-3 w-full">
                <button
                  onClick={this.handleReload}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reload Page
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  <Home className="w-4 h-4" />
                  Go Home
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// MAP ERROR BOUNDARY
// ============================================================================

interface MapErrorBoundaryProps {
  children: ReactNode;
  mapName?: string;
}

/**
 * Map Error Boundary
 * Specialized error boundary for map components (Leaflet, Google Maps, etc.)
 */
export class MapErrorBoundary extends Component<MapErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: MapErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Map rendering error', {
      mapName: this.props.mapName,
      error,
      errorInfo,
    });
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-6 bg-gray-100 rounded-lg border border-gray-300 min-h-[300px]">
          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-3">
            <AlertTriangle className="w-6 h-6 text-gray-500" />
          </div>
          <h4 className="text-base font-semibold text-gray-900 mb-1">
            Map unavailable
          </h4>
          <p className="text-sm text-gray-600 text-center max-w-sm">
            {this.props.mapName || 'The map'} could not be loaded. This might be due to missing data or a network issue.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default ErrorBoundary;
